import { useState, useEffect, useId } from 'react';
import { Button, TextButton } from '@safetyhub/ui';
import { verificationApi, extractApiError } from '../lib/auth';
import { formatCountdown } from '../lib/countdown';

// PhoneVerification과 대칭인 이메일 인증 흐름(코드 발송 → 확인 → onVerified 토큰 전달).
// 프로필의 이메일 등록·변경이 emailVerificationToken을 요구하게 되면서 필요해졌다.
interface EmailVerificationProps {
  email: string;
  onEmailChange: (email: string) => void;
  onVerified: (verificationToken: string) => void;
  disabled?: boolean;
  /**
   * 주소 입력칸 위 라벨. 기본값 '이메일'은 기존 호출부(계정 이메일)를 그대로 두기 위한 것이고,
   * 연락처 이메일처럼 같은 흐름을 다른 대상에 쓸 때만 넘긴다.
   */
  label?: string;
}

export default function EmailVerification({
  email,
  onEmailChange,
  onVerified,
  disabled = false,
  label = '이메일',
}: EmailVerificationProps) {
  const [step, setStep] = useState<'input' | 'verify' | 'done'>('input');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);

  // 라벨↔입력칸 연결용 id. 프로필 화면이 이 컴포넌트를 **두 번**(계정 이메일 / 연락처 이메일)
  // 띄울 수 있게 되면서, 고정 문자열 id를 쓰면 문서에 중복 id가 생긴다 — 스크린리더와
  // "라벨 클릭 → 입력칸 포커스"가 둘 다 첫 번째 인스턴스로 끌려간다. useId는 인스턴스마다
  // 다른 값을 준다.
  const emailInputId = useId();
  const codeInputId = useId();

  // 타이머
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 이메일 유효성 검사 (profile 페이지와 같은 기준)
  const isValidEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  };

  // 인증 코드 발송
  const sendCode = async () => {
    if (!isValidEmail(email)) {
      setError('올바른 이메일 형식이 아닙니다.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await verificationApi.send({
        type: 'EMAIL_VERIFY',
        email: email.trim(),
      });
      setStep('verify');
      setCountdown(180); // 3분 (서버 EMAIL_VERIFY 만료와 동일)
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 인증 코드 확인
  const verifyCode = async () => {
    if (code.length !== 6) {
      setError('6자리 인증번호를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await verificationApi.verify({
        type: 'EMAIL_VERIFY',
        email: email.trim(),
        code,
      });
      setStep('done');
      onVerified(result.verificationToken);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 재발송
  const resendCode = async () => {
    if (countdown > 0) return;
    await sendCode();
  };

  if (step === 'done') {
    return (
      <div className="space-y-2">
        <label htmlFor={emailInputId} className="block text-sm font-medium text-ink-700">{label}</label>
        <div className="flex items-center space-x-2">
          <input
            id={emailInputId}
            type="email"
            value={email}
            disabled
            className="flex-1 px-3 py-2 border border-ink-300 rounded-control bg-ink-100 text-ink-700"
          />
          <span className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-control">
            <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            인증완료
          </span>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (code.length === 6 && !disabled && !isLoading) {
          verifyCode();
        }
      }
    };

    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor={emailInputId} className="block text-sm font-medium text-ink-700">{label}</label>
          <input
            id={emailInputId}
            type="email"
            value={email}
            disabled
            className="w-full px-3 py-2 border border-ink-300 rounded-control bg-ink-100 text-ink-700"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor={codeInputId} className="block text-sm font-medium text-ink-700">인증번호</label>
          <div className="flex space-x-2">
            <div className="relative flex-1">
              <input
                id={codeInputId}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={handleKeyDown}
                disabled={disabled || isLoading}
                className="w-full px-3 py-2 border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500"
                placeholder="6자리 입력"
              />
              {countdown > 0 && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-red-500">
                  {formatCountdown(countdown)}
                </span>
              )}
            </div>
            <Button
              variant="primary"
              onClick={verifyCode}
              disabled={disabled || isLoading || code.length !== 6}
            >
              {isLoading ? '확인 중...' : '확인'}
            </Button>
          </div>
          <div className="flex justify-between items-center">
            <TextButton
              onClick={resendCode}
              disabled={disabled || isLoading || countdown > 0}
              className="text-sm"
            >
              {countdown > 0 ? `${formatCountdown(countdown)} 후 재발송 가능` : '인증번호 재발송'}
            </TextButton>
            <TextButton
              tone="neutral"
              onClick={() => {
                setStep('input');
                setCode('');
                setCountdown(0);
              }}
              className="text-sm"
            >
              이메일 변경
            </TextButton>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label htmlFor={emailInputId} className="block text-sm font-medium text-ink-700">{label}</label>
      <div className="flex space-x-2">
        <input
          id={emailInputId}
          type="email"
          value={email}
          onChange={(e) => {
            onEmailChange(e.target.value);
            setError('');
          }}
          disabled={disabled || isLoading}
          className={`flex-1 px-3 py-2 border rounded-control focus:ring-brand-500 focus:border-brand-500 ${
            error ? 'border-red-300' : 'border-ink-300'
          }`}
          placeholder="이메일 주소를 입력하세요"
        />
        <Button
          variant="primary"
          onClick={sendCode}
          disabled={disabled || isLoading || !isValidEmail(email)}
          className="whitespace-nowrap"
        >
          {isLoading ? '발송 중...' : '인증번호 받기'}
        </Button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
